'use client';

import { useId } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Sparkles } from 'lucide-react';

import { SheetClose } from '@/components/ui/sheet';
import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';
import { yourImaiHref } from '@/lib/your-imai-routes';

import {
  PostHogSupportOptionContent,
  postHogSupportOptionClassName,
} from './posthog-support-option';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

/** Your IMAI in the current workspace; null outside one. */
export function useAskImaiHref(): string | null {
  const params = useParams<{ orgSlug?: string | string[] }>();
  const slug = Array.isArray(params?.orgSlug)
    ? params.orgSlug[0]
    : params?.orgSlug;
  return slug ? yourImaiHref(slug) : null;
}

/**
 * Ask IMAI: an answer now, in the member's Your IMAI chat. It is a plain link
 * that never depends on the support provider, so it shows even when support
 * is unavailable. In the support sheet (`dialog`), following it closes the
 * sheet. Renders nothing outside a workspace.
 */
export function PostHogSupportAskImai({
  className,
  compact,
  copy,
  dialog,
}: {
  className?: string;
  compact: boolean;
  copy: SupportCopy;
  dialog: boolean;
}) {
  const descriptionId = useId();
  const href = useAskImaiHref();
  if (!href) return null;
  const link = (
    <Link
      href={href}
      aria-describedby={descriptionId}
      data-support-ask-imai
      className={cn(postHogSupportOptionClassName(compact, 'row'), className)}
    >
      <PostHogSupportOptionContent
        compact={compact}
        description={copy.askImaiDescription}
        descriptionId={descriptionId}
        icon={Sparkles}
        label={copy.askImai}
        layout="row"
      />
    </Link>
  );
  return dialog ? (
    // Not the sheet's own close control, only a link that closes it.
    <SheetClose asChild data-slot="support-ask-imai">
      {link}
    </SheetClose>
  ) : (
    link
  );
}
